import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Grid, Typography, CircularProgress, Alert, Paper,
  IconButton, Stack, Skeleton, Fade, alpha,
  Avatar, keyframes,
  Dialog, DialogContent, DialogActions, Button, Chip, Divider,
} from '@mui/material';
import {
  Inventory2 as ProductIcon,
  Payment as PaymentIcon,
  OpenInNew as OpenInNewIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
} from '@mui/icons-material';
import { Title, useGetList } from 'react-admin';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import authInstance from '../services/httpClient';
import { usePermissions } from '../context/PermissionContext';
import { useAdminTheme } from '../admin-theme';
import { Band, monoLabelSx } from '../components/reskin/primitives';
import { useSocketIO } from '../providers/SocketIOProvider';
import { FONT_DATA, FONT_DISPLAY } from '../theme/grounds';
import Logger from '../utils/Logger';
import { phaseOf, PHASE_BY_KEY } from './LiveOrders/phases';
import { minutesSince, orderCode } from './LiveOrders/theme';
import type { AdminOrder } from './LiveOrders/types';

import { useRecentSignups, NewCustomerInfo, NewCustomerOverview } from './components/NewCustomerCards';

import ozowLogo from '../assets/payments/ozow.png';
import snapscanLogo from '../assets/payments/snapscan.png';
import zapperLogo from '../assets/payments/zapper.png';
import paygateLogo from '../assets/payments/paygate.png';
import payjustnowLogo from '../assets/payments/payjustnow.png';
import payflexLogo from '../assets/payments/payflex.svg';

// ============================================================================
// Types
// ============================================================================

type Period = 'today' | 'week' | 'month' | 'ytd';

interface StoresSummary {
  totalStores: number;
  activeStores: number;
  openStores: number;
  pausedStores: number;
}

interface TopProduct {
  productId: string;
  sku: string | null;
  name: string;
  quantity: number;
  revenue: number;
  imageUrl: string | null;
}

/** Catalogue detail for the quick-view modal (subset of admin/tools/products). */
interface ProductDetail {
  id: string;
  sku: string;
  name: string;
  category: string;
  brand: string | null;
  priceTier1: number;
  shortDescription: string;
  status: string;
  sellable: boolean;
  tags: string[];
  imageUrl: string | null;
  stock: { available?: number; onHand?: number } | null;
}

/** The five KPIs for one window. */
interface PeriodKpis {
  totalOrders: number;
  totalRevenue: number;
  avgCart: number;
  avgDeliverySeconds: number | null;
  deliveredCount: number;
  newCustomers: number;
}

interface StatsData {
  period: Period;
  periodStart: string;
  monthStart: string;

  // KPIs — all follow the selected period
  totalOrders: number;
  totalRevenue: number;
  avgCart: number;
  avgDeliverySeconds: number | null;
  deliveredCount: number;
  newCustomers: number;
  /** The same five KPIs for every window at once — the stat cards read these. */
  periods?: Record<Period, PeriodKpis>;

  ordersByStatus: Record<string, number>;
  deliveries: { active: number; completed: number };
  topStores: Array<{ storeId: string; name: string; orders: number; revenue: number; isHq?: boolean }>;
  paymentMethods: Array<{ method: string; count: number; share: number }>;
  topProducts: TopProduct[];
  generatedAt: string;
}

interface RealtimeData {
  ios: number;
  android: number;
  web: number;
  total: number;
}

interface RealtimePagesData {
  total: number;
  pages: Array<{ name: string; views: number }>;
}

// ============================================================================
// Constants
// ============================================================================

/** The four cells of every stat card, in reading order. */
const PERIOD_CELLS: Array<{ key: Period; label: string }> = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'ytd', label: 'YTD' },
];

/** The operational boards this page summarises — the landing page should reach them. */
const QUICK_LINKS: Array<{ to: string; label: string; hint: string }> = [
  // Ordered by how often they are reached, most-used first — this is the landing page,
  // so the top-left tile should be the one people click all day.
  { to: '/live-orders', label: 'Live Orders', hint: 'Everything in flight right now' },
  { to: '/orders', label: 'Orders', hint: 'Full order history and search' },
  { to: '/stores/command-center', label: 'Store Command Center', hint: 'Health, hours and config per store' },
  { to: '/content/control', label: 'Content Control', hint: 'Banners, categories and brands' },
  { to: '/products', label: 'Deeliver Products', hint: 'Catalogue, pricing and availability' },
  { to: '/customers', label: 'Customers', hint: 'Accounts, orders and activity' },
];

const PAYMENT_LABELS: Record<string, string> = {
  ozow: 'Ozow',
  paygate: 'PayGate',
  snapscan: 'SnapScan',
  zapper: 'Zapper',
  payflex: 'Payflex',
  payjustnow: 'PayJustNow',
};

/** Logos lifted from the mobile app's checkout icons. */
const PAYMENT_LOGOS: Record<string, string> = {
  ozow: ozowLogo,
  paygate: paygateLogo,
  snapscan: snapscanLogo,
  zapper: zapperLogo,
  payflex: payflexLogo,
  payjustnow: payjustnowLogo,
};

/** Rows per page on the three month-to-date lists — the prototype's `data-page-size`. */
const PAGE_SIZE = 6;
/** Top Selling Items has the room for more rows than the other two month cards (Jason, 2026-09-14). */
const SELLERS_PAGE_SIZE = 10;

/** How many cards the Live Orders feed shows — the head of the board. */
const LIVE_ORDER_CARDS = 4;

/**
 * Stroke glyphs for the card headers and the Live Users rows, lifted from the
 * reskin prototype (24-box, 1.9px stroke, no fill). Kept here rather than in
 * navGlyphs.tsx because these are the dashboard's, not the rail's.
 */
const GLYPH = {
  users: '<circle cx="9" cy="8" r="3"/><path d="M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5"/><path d="M16 11a3 3 0 0 0 0-6"/><path d="M18 20c0-2.4-.9-4.2-2.4-5.2"/>',
  cart: '<circle cx="9" cy="20" r="1.4"/><circle cx="18" cy="20" r="1.4"/><path d="M2 3h3l2.6 12h11L21 7H6"/>',
  revenue: '<path d="M12 3v18"/><path d="M16.5 7.5A3.5 3.5 0 0 0 13 5h-1.6a2.9 2.9 0 0 0 0 5.8h1.2a2.9 2.9 0 0 1 0 5.8H11a3.5 3.5 0 0 1-3.5-2.5"/>',
  basket: '<path d="M3 9h18l-1.6 10.2a2 2 0 0 1-2 1.8H6.6a2 2 0 0 1-2-1.8L3 9z"/><path d="M8.5 9L12 3.5 15.5 9"/>',
  timer: '<circle cx="12" cy="13" r="8"/><path d="M12 9v4l2.5 2"/><path d="M9 2h6"/>',
  signup: '<circle cx="10" cy="8" r="3.2"/><path d="M3 20c0-3.5 3-5.8 7-5.8"/><path d="M17 12v6M14 15h6"/>',
  pie: '<path d="M12 3a9 9 0 1 0 9 9h-9V3z"/><path d="M14.5 2.5A8 8 0 0 1 21.5 9.5H14.5z"/>',
  card: '<rect x="2.5" y="5.5" width="19" height="13" rx="2"/><path d="M2.5 10h19"/>',
  store: '<path d="M4 9h16v11H4z"/><path d="M3 9l1.6-5h14.8L21 9"/><path d="M9 20v-6h6v6"/>',
  layers: '<path d="M4 7l8-4 8 4-8 4-8-4z"/><path d="M4 12l8 4 8-4"/><path d="M4 17l8 4 8-4"/>',
  eye: '<path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/>',
  apple: '<g transform="scale(0.048)"><path d="M242.2608 158.5154c-17.7233 0-45.158-19.3118-74.0494-18.6138-38.1172.4654-73.0782 21.1732-92.7438 53.98-39.574 65.8462-10.197 163.1033 28.4058 216.6179 18.9373 26.0593 41.2735 55.376 70.8932 54.4453 28.4058-1.1634 39.0884-17.683 73.5638-17.683 34.2327 0 43.944 17.683 74.0494 16.985 30.5909-.4654 50.0137-26.5247 68.7081-52.8166 21.6079-30.2474 30.5909-59.5641 31.0765-61.1928-.7284-.2327-59.4823-21.8712-60.2107-87.0195-.4855-54.4453 46.372-80.5046 48.557-81.668-26.7063-37.4602-67.737-41.6483-82.0613-42.579-37.3888-2.792-68.708 19.5445-86.1886 19.5445zm63.124-54.9107c15.781-18.1484 26.2208-43.5097 23.3074-68.6383-22.579.9307-49.7709 14.4257-66.0375 32.5741-14.567 16.0544-27.1919 41.881-23.7929 66.5443 25.0069 1.8614 50.742-12.3316 66.523-30.48z"/></g>',
  android: '<path fill-rule="evenodd" d="M6.23 8.05a5.77 5.77 0 0 1 11.54 0zM9.23 5.05a.43.43 0 1 0 0 .86.43.43 0 0 0 0-.86zm5.27 0a.43.43 0 1 0 0 .86.43.43 0 0 0 0-.86z"/><path d="M8.97 3.33 8.16 1.99M14.93 3.33l.8-1.34" stroke="currentColor" stroke-width=".5" stroke-linecap="round"/><path d="M6.17 8.59h11.55v8.31a1.34 1.34 0 0 1-1.34 1.34H7.51a1.34 1.34 0 0 1-1.34-1.34z"/><rect x="3.33" y="8.32" width="2.41" height="7.78" rx="1.2"/><rect x="18.15" y="8.32" width="2.41" height="7.78" rx="1.2"/><path d="M8.48 18.25H11v2.77a1.26 1.26 0 0 1-2.52 0zM12.88 18.25h2.52v2.77a1.26 1.26 0 0 1-2.52 0z"/>',
} as const;

type GlyphName = keyof typeof GLYPH;

/** Apple and Android are filled marks; everything else is a stroke. */
const FILLED_GLYPHS: GlyphName[] = ['apple', 'android'];

const Glyph: React.FC<{ name: GlyphName; size?: number }> = ({ name, size = 14 }) => {
  const filled = FILLED_GLYPHS.includes(name);
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill={filled ? 'currentColor' : 'none'}
      stroke={filled ? 'none' : 'currentColor'}
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      style={{ display: 'block', flex: 'none' }}
      dangerouslySetInnerHTML={{ __html: GLYPH[name] }}
    />
  );
};

/** Blinking glow on the "live" dot. */
const liveBlink = keyframes`
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.55; }
`;

/** Cell flashes when its number changes. */
const cellPulse = (color: string) => keyframes`
  0%   { box-shadow: 0 0 0 0 ${alpha(color, 0.55)}; }
  25%  { box-shadow: 0 0 0 4px ${alpha(color, 0)}; background: ${alpha(color, 0.14)}; }
  100% { box-shadow: 0 0 0 0 ${alpha(color, 0)}; }
`;

// ============================================================================
// Formatting
// ============================================================================

const formatCurrency = (n: number) =>
  `R${n.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Whole-rand money for the stat cells, compacting past six figures the way the
 * prototype does: R67 068 · R441 900 · R1.77m · R23.4m.
 */
function formatMoneyCompact(n: number): string {
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return `R${m >= 10 ? m.toFixed(1) : m.toFixed(2)}m`;
  }
  return `R${Math.round(n).toLocaleString('en-ZA')}`;
}

const formatCount = (n: number | null | undefined) => (typeof n === 'number' && Number.isFinite(n) ? n.toLocaleString('en-ZA') : '—');

/** Seconds -> compact human duration. null means nothing was delivered in the window. */
function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const totalMinutes = Math.round(seconds / 60);
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
}

/** "2m" / "1h 20m" / "2d" — the Live Orders card timer. */
function formatAge(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return minutes % 60 ? `${hours}h ${minutes % 60}m` : `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

// ============================================================================
// Shared bits of the skin
// ============================================================================

/** Header link — "Open board →", "View all →". */
const CardLink: React.FC<{ onClick: () => void; children: React.ReactNode }> = ({ onClick, children }) => {
  const { colors } = useAdminTheme();
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        font: 'inherit',
        fontSize: '0.75rem',
        color: colors.textMuted,
        background: 'none',
        border: 0,
        p: 0,
        cursor: 'pointer',
        transition: 'color .15s',
        '&:hover': { color: colors.info },
      }}
    >
      {children}
    </Box>
  );
};

/** Card shell — the prototype's `.card` + `.card-hd`: ember tile, display title, mono subtitle. */
const StatBox: React.FC<{
  title: string;
  subtitle?: string;
  glyph: GlyphName;
  headerRight?: React.ReactNode;
  children: React.ReactNode;
  /** Let the body stretch (paged lists pin their pager to the bottom). */
  column?: boolean;
}> = ({ title, subtitle, glyph, headerRight, children, column }) => {
  const { colors, cardStyle } = useAdminTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        ...cardStyle,
        p: '16px 17px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.4} sx={{ mb: 1.9 }}>
        <Box
          sx={{
            width: 26, height: 26, borderRadius: '7px', flex: 'none',
            display: 'grid', placeItems: 'center',
            bgcolor: 'var(--ember-bg)', border: '1px solid var(--ember-line)', color: colors.info,
          }}
        >
          <Glyph name={glyph} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontFamily: FONT_DISPLAY, fontStretch: '110%', fontWeight: 700, fontSize: '0.97rem', color: colors.textPrimary, lineHeight: 1.2, letterSpacing: '-0.008em' }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontFamily: FONT_DATA, fontSize: '0.6875rem', color: colors.textMuted, mt: '2px' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {headerRight}
      </Stack>
      <Box sx={{ flex: 1, minHeight: 0, display: column ? 'flex' : 'block', flexDirection: 'column' }}>{children}</Box>
    </Paper>
  );
};

/* Top Performing Stores marks, served from public/: Deeliver's own store (Diep River) shows
   the Deeliver D, every Vaperite branch the official Vaperite flame (from their own PDF,
   1 Nov 2026; it replaced a hand-traced copy). */
const DEELIVER_MARK = '/deeliver-d.png';
const VAPERITE_EMBLEM = '/vaperite-emblem.png';

/**
 * A white tile holding a partner's mark — payment gateways and, with the same
 * box, the store's mark (Deeliver D or Vaperite flame) beside each store. 78x44: the gateway logos are
 * wordmarks, so the tile is wide rather than square, or they squash (Jason,
 * 2026-09-14 — started at 38x26).
 */
const LogoTile: React.FC<{ src?: string; alt: string; fallback: React.ReactNode }> = ({ src, alt, fallback }) => {
  const { colors } = useAdminTheme();
  return (
    <Box sx={{
      width: 78, height: 44, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      borderRadius: '5px', bgcolor: '#FFFFFF', border: `1px solid ${colors.borderGlow}`, p: '5px 7px',
    }}>
      {src ? (
        <Box component="img" src={src} alt={alt} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
      ) : fallback}
    </Box>
  );
};

const EmptyNote: React.FC<{ children: React.ReactNode; height?: number }> = ({ children, height = 160 }) => {
  const { colors } = useAdminTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height }}>
      <Typography variant="body2" sx={{ color: colors.textMuted }}>{children}</Typography>
    </Box>
  );
};

/** A raised cell with a value over a label — the prototype's `.statcell`. */
const StatCell: React.FC<{ value: string; label: string }> = ({ value, label }) => {
  const { colors } = useAdminTheme();
  return (
    <Box
      sx={{
        bgcolor: colors.bgElevated,
        border: `1px solid ${colors.border}`,
        borderRadius: '10px',
        p: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      <Typography sx={{ fontSize: '1rem', fontWeight: 650, letterSpacing: '-0.01em', color: colors.textPrimary, lineHeight: 1.25, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: '1rem', color: colors.textSecondary, lineHeight: 1.25 }}>
        {label}
      </Typography>
    </Box>
  );
};

/** One metric across the four windows, as a 2×2 of cells. */
const StatCard: React.FC<{
  title: string;
  glyph: GlyphName;
  periods: Record<Period, PeriodKpis> | undefined;
  pick: (k: PeriodKpis) => string;
}> = ({ title, glyph, periods, pick }) => (
  <StatBox title={title} glyph={glyph} column>
    <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gridAutoRows: '1fr', gap: 1.5 }}>
      {PERIOD_CELLS.map(({ key, label }) => (
        <StatCell key={key} value={periods?.[key] ? pick(periods[key]) : '—'} label={label} />
      ))}
    </Box>
  </StatBox>
);

/**
 * Page through a list, PAGE_SIZE rows at a time, with the pager pinned to the
 * bottom of the card. One page still reserves the pager row, so the three
 * month-to-date cards keep their content aligned.
 */
function usePaged<T>(items: T[], size = PAGE_SIZE) {
  const [page, setPage] = useState(0);
  const pages = Math.max(1, Math.ceil(items.length / size));
  const current = Math.min(page, pages - 1);
  const slice = items.slice(current * size, current * size + size);
  return { slice, page: current, pages, setPage, offset: current * size };
}

const Pager: React.FC<{ page: number; pages: number; onChange: (p: number) => void }> = ({ page, pages, onChange }) => {
  const { colors } = useAdminTheme();
  const btn = {
    width: 28, height: 28, borderRadius: '8px', p: 0,
    border: `1px solid ${colors.borderGlow}`, bgcolor: colors.bgElevated, color: colors.textSecondary,
    '&:hover': { color: colors.info, borderColor: colors.info, bgcolor: colors.bgElevated },
    '&.Mui-disabled': { opacity: 0.35, color: colors.textSecondary },
  };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, pt: 1.5, mt: 'auto', visibility: pages > 1 ? 'visible' : 'hidden' }}>
      <IconButton size="small" onClick={() => onChange(page - 1)} disabled={page === 0} sx={btn} aria-label="Previous page">
        <PrevIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Typography sx={{ fontFamily: FONT_DATA, fontSize: '0.75rem', color: colors.textSecondary, minWidth: 54, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
        {page + 1} / {pages}
      </Typography>
      <IconButton size="small" onClick={() => onChange(page + 1)} disabled={page >= pages - 1} sx={btn} aria-label="Next page">
        <NextIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
};

/** The index column on ranked rows. */
const RankIdx: React.FC<{ n: number; width?: number }> = ({ n, width = 14 }) => {
  const { colors } = useAdminTheme();
  return (
    <Typography sx={{ fontFamily: FONT_DATA, fontSize: '0.72rem', fontWeight: 700, color: colors.textMuted, width, flex: 'none', fontVariantNumeric: 'tabular-nums' }}>
      {n}
    </Typography>
  );
};

// ============================================================================
// Live Users — GA4 realtime active users, last 30 minutes.
// ============================================================================

/**
 * Deliberately app-only: iOS + Android, no Website tile. The backend's `total`
 * is iOS + Android for that reason, so the tiles add up.
 *
 * Each row's mark sits on its own small tile in the brand's colour (Jason,
 * 2026-09-20, from his attached marks): the Apple logo black on white — the
 * only way black reads on the dark grounds — the classic bugdroid in its
 * green (#79C257, sampled from the attachment), and Total in a blue that sits
 * with the two. The tiles are fixed colours on purpose; they are the
 * brands' marks, not the ground's.
 */
const LIVE_CELLS: Array<{ label: string; key: keyof RealtimeData; glyph: GlyphName; fg: string; bg: string }> = [
  { label: 'Apple', key: 'ios', glyph: 'apple', fg: '#000000', bg: '#FFFFFF' },
  { label: 'Android', key: 'android', glyph: 'android', fg: '#79C257', bg: alpha('#79C257', 0.16) },
  { label: 'Total', key: 'total', glyph: 'users', fg: '#4C8DFF', bg: alpha('#4C8DFF', 0.16) },
];

const LiveUsersBox: React.FC = () => {
  const { colors } = useAdminTheme();
  const [data, setData] = useState<RealtimeData | null>(null);
  const [error, setError] = useState(false);
  const [pulsing, setPulsing] = useState<Set<keyof RealtimeData>>(new Set());
  const prev = useRef<RealtimeData | null>(null);
  const alive = useRef(true);
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    alive.current = true;

    const load = async () => {
      if (document.visibilityState === 'hidden') return; // don't poll a hidden tab
      try {
        const { data: next } = await authInstance.get<RealtimeData>('admin/analytics/realtime');
        if (!alive.current) return;
        setError(false);

        // Flash any cell whose number moved since the last poll.
        if (prev.current) {
          const changed = LIVE_CELLS
            .map((c) => c.key)
            .filter((k) => next[k] !== prev.current![k]);

          if (changed.length) {
            setPulsing((cur) => {
              const s = new Set(cur);
              changed.forEach((k) => s.add(k));
              return s;
            });
            changed.forEach((k) => {
              clearTimeout(timers.current[k]);
              timers.current[k] = setTimeout(() => {
                setPulsing((cur) => {
                  const s = new Set(cur);
                  s.delete(k);
                  return s;
                });
              }, 1200);
            });
          }
        }

        prev.current = next;
        setData(next);
      } catch (err) {
        Logger.error('Failed to fetch GA4 realtime:', err);
        if (alive.current) setError(true);
      }
    };

    load();
    const interval = setInterval(load, 30000);
    const onVisible = () => document.visibilityState === 'visible' && load();
    document.addEventListener('visibilitychange', onVisible);

    const pending = timers.current;
    return () => {
      alive.current = false;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      Object.values(pending).forEach(clearTimeout);
    };
  }, []);

  const liveTag = error ? (
    <Typography sx={{ ...monoLabelSx, fontSize: '0.6875rem', letterSpacing: '0.11em', color: colors.error }}>
      error
    </Typography>
  ) : (
    <LiveTag />
  );

  return (
    <StatBox title="Live Users" glyph="users" headerRight={liveTag} column>
      {error ? (
        <EmptyNote>Realtime data unavailable</EmptyNote>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '9px', flex: 1 }}>
            {LIVE_CELLS.map(({ label, key, glyph, fg, bg }) => {
              const total = key === 'total';
              return (
                <Box
                  key={key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '13px',
                    flex: 1,
                    minHeight: 52,
                    px: '15px',
                    bgcolor: total ? 'var(--ok-bg)' : colors.bgElevated,
                    border: `1px solid ${total ? 'var(--ok-line)' : colors.borderGlow}`,
                    borderRadius: '8px',
                    ...(pulsing.has(key) && {
                      animation: `${cellPulse(colors.success)} 1.2s cubic-bezier(.2,.7,.2,1)`,
                      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                    }),
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '8px',
                      display: 'grid',
                      placeItems: 'center',
                      color: fg,
                      bgcolor: bg,
                      flex: 'none',
                    }}
                  >
                    <Glyph name={glyph} size={18} />
                  </Box>
                  <Typography sx={{ ...monoLabelSx, flex: 1, minWidth: 0, fontSize: '0.72rem', color: colors.textMuted }}>
                    {label}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: FONT_DISPLAY,
                      fontStretch: '125%',
                      fontWeight: 800,
                      fontSize: '1.7rem',
                      lineHeight: 1,
                      letterSpacing: '-0.035em',
                      fontVariantNumeric: 'tabular-nums',
                      color: total || pulsing.has(key) ? colors.success : colors.textPrimary,
                    }}
                  >
                    {data ? data[key] : '—'}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          <Typography sx={{ fontFamily: FONT_DATA, fontSize: '0.6875rem', color: colors.textMuted, mt: '13px' }}>
            Active users · last 30 min (GA4 realtime)
          </Typography>
        </Box>
      )}
    </StatBox>
  );
};

// ============================================================================
// Live Pages — GA4 realtime views by page title / screen name, last 30 minutes.
// ============================================================================

/** Rows on the card; the backend sends at most this many. */
const LIVE_PAGES_ROWS = 10;

/**
 * The GA app's Realtime "Views by Page title and screen name" table, top 10,
 * copied from the Command Center's Business · Live Pages card (Jason,
 * 2026-10-25). "(not set)" — GA's unnamed-screen bucket — is always last.
 * Short rows are padded with blank ones, so the card keeps its height while
 * loading and on a quiet half hour.
 */
const LivePagesBox: React.FC = () => {
  const { colors } = useAdminTheme();
  const [data, setData] = useState<RealtimePagesData | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      if (document.visibilityState === 'hidden') return; // don't poll a hidden tab
      try {
        const { data: next } = await authInstance.get<RealtimePagesData>('admin/analytics/realtime-pages');
        if (!alive) return;
        setError(false);
        setData(next);
      } catch (err) {
        Logger.error('Failed to fetch GA4 realtime pages:', err);
        if (alive) setError(true);
      }
    };

    load();
    const interval = setInterval(load, 60000);
    const onVisible = () => document.visibilityState === 'visible' && load();
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      alive = false;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const pages = (data?.pages ?? []).slice(0, LIVE_PAGES_ROWS);

  const liveTag = error ? (
    <Typography sx={{ ...monoLabelSx, fontSize: '0.6875rem', letterSpacing: '0.11em', color: colors.error }}>
      error
    </Typography>
  ) : (
    <LiveTag />
  );

  const rowSx = { display: 'flex', alignItems: 'center', gap: 1.25, minHeight: 26 };

  return (
    <StatBox title="Live Pages" glyph="eye" headerRight={liveTag} column>
      {error ? (
        <EmptyNote>Realtime data unavailable</EmptyNote>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ ...rowSx, minHeight: 0, pb: '6px', mb: '4px', borderBottom: `1px solid ${colors.border}` }}>
            <Typography sx={{ ...monoLabelSx, flex: 1, fontSize: '0.6875rem', color: colors.textMuted }}>Page / screen</Typography>
            <Typography sx={{ ...monoLabelSx, fontSize: '0.6875rem', color: colors.textMuted }}>Views</Typography>
          </Box>
          <Box sx={{ position: 'relative', flex: 1 }}>
            {pages.map((p, i) => (
              <Box key={`${i}-${p.name}`} sx={rowSx}>
                <RankIdx n={i + 1} width={18} />
                <Typography
                  title={p.name}
                  sx={{ fontSize: '0.84rem', fontWeight: 600, color: colors.textPrimary, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {p.name}
                </Typography>
                <Typography sx={{ fontFamily: FONT_DATA, fontSize: '0.8125rem', fontWeight: 700, color: colors.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
                  {p.views.toLocaleString('en-ZA')}
                </Typography>
              </Box>
            ))}
            {Array.from({ length: LIVE_PAGES_ROWS - pages.length }, (_, i) => (
              <Box key={`pad-${i}`} sx={rowSx} aria-hidden />
            ))}
            {(!data || pages.length === 0) && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" sx={{ color: colors.textMuted }}>
                  {data ? 'No views in the last 30 min' : 'Loading…'}
                </Typography>
              </Box>
            )}
          </Box>
          <Typography sx={{ fontFamily: FONT_DATA, fontSize: '0.6875rem', color: colors.textMuted, mt: '13px' }}>
            {data ? `${data.total.toLocaleString('en-ZA')} views · last 30 min (GA4 realtime)` : 'Views · last 30 min (GA4 realtime)'}
          </Typography>
        </Box>
      )}
    </StatBox>
  );
};

/**
 * The "● live" tag on a card header. With an onClick it is a button (Live
 * Orders opens its board); without one it is just the indicator.
 */
const LiveTag: React.FC<{ onClick?: () => void }> = ({ onClick }) => {
  const { colors } = useAdminTheme();
  const sx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    textDecoration: 'none',
    ...monoLabelSx,
    fontSize: '0.6875rem',
    letterSpacing: '0.11em',
    color: colors.success,
    background: 'none',
    border: 0,
    p: 0,
    cursor: onClick ? 'pointer' : 'default',
    lineHeight: 1,
  } as const;
  const dot = (
    <Box
      sx={{
        width: 7, height: 7, borderRadius: '50%', bgcolor: colors.success, flex: 'none',
        animation: `${liveBlink} 2s ease-in-out infinite`,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    />
  );
  return onClick ? (
    <Box component="button" type="button" onClick={onClick} sx={sx}>{dot}live</Box>
  ) : (
    <Box component="span" sx={sx}>{dot}live</Box>
  );
};

// ============================================================================
// Live Orders — the head of the Live Orders board, newest first.
// ============================================================================

const isUber = (o: AdminOrder) => /uber/i.test(o.channel || o.source || '') || !!o.uberDirectId;

const LiveOrdersBox: React.FC = () => {
  const { colors } = useAdminTheme();
  const navigate = useNavigate();
  const { onNewOrder } = useSocketIO();

  // Every store — this is the landing page, not a store-scoped board. The data
  // provider falls back to demo orders in demo mode when there are none.
  const { data, isLoading, refetch } = useGetList<AdminOrder>(
    'orders',
    { pagination: { page: 1, perPage: 20 }, sort: { field: 'createdAt', order: 'DESC' } },
    { staleTime: 30_000, refetchOnWindowFocus: false, refetchInterval: 60_000 },
  );

  // A new order pushed over the socket refreshes the feed.
  useEffect(() => onNewOrder(() => { refetch(); }), [onNewOrder, refetch]);

  const head = useMemo(
    () => (data ?? []).filter((o) => phaseOf(o.status) !== 'cancelled').slice(0, LIVE_ORDER_CARDS),
    [data],
  );

  const openBoard = () => navigate('/live-orders');

  return (
    <StatBox title="Live Orders" glyph="cart" headerRight={<LiveTag onClick={openBoard} />} column>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        {isLoading && !data ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.25 }}>
            {Array.from({ length: LIVE_ORDER_CARDS }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={112} sx={{ bgcolor: colors.bgElevated }} />
            ))}
          </Box>
        ) : head.length === 0 ? (
          <EmptyNote height={120}>Nothing on the board right now</EmptyNote>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.25, alignItems: 'start' }}>
            {head.map((o) => {
              const phase = phaseOf(o.status);
              const mins = minutesSince(o.createdAt);
              const uber = isUber(o);
              const items = (o.lines ?? []).reduce((n, l) => n + (l.quantity || 0), 0);
              const customer = `${o.user?.firstName ?? ''} ${o.user?.lastName ?? ''}`.trim() || 'Customer';
              return (
                <Box
                  key={o.id}
                  role="button"
                  tabIndex={0}
                  onClick={openBoard}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openBoard(); } }}
                  sx={{
                    bgcolor: colors.bgCard,
                    border: `1px solid ${colors.borderGlow}`,
                    borderLeft: `3px solid ${PHASE_BY_KEY[phase].pin}`,
                    borderRadius: '8px',
                    p: '10px',
                    cursor: 'pointer',
                    transition: 'border-color .15s',
                    '&:hover': { borderColor: colors.info, borderLeftColor: PHASE_BY_KEY[phase].pin },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: '6px' }}>
                    <Box
                      sx={{
                        width: 22, height: 22, borderRadius: '6px', display: 'grid', placeItems: 'center', flex: 'none',
                        fontWeight: 700, fontSize: '0.72rem',
                        bgcolor: uber ? '#111' : '#34eec1', color: uber ? '#fff' : '#06231d',
                      }}
                    >
                      {uber ? 'U' : 'D'}
                    </Box>
                    <Typography sx={{ fontFamily: FONT_DATA, fontWeight: 700, fontSize: '0.8125rem', color: colors.textPrimary }}>
                      {orderCode(o.number, o.id)}
                    </Typography>
                    <Typography
                      sx={{
                        ml: 'auto', fontWeight: 700, fontSize: '0.78rem',
                        color: phase === 'delivered' ? colors.textMuted : mins > 20 ? colors.error : mins > 11 ? colors.warning : colors.textMuted,
                      }}
                    >
                      {formatAge(mins)}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.84rem', color: colors.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {customer}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: colors.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {o.store?.name ?? o.storeId}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: '7px', gap: 1 }}>
                    <Typography sx={{ fontSize: '0.75rem', color: colors.textMuted, flex: 1 }}>
                      Delivery · {items} item{items === 1 ? '' : 's'}
                    </Typography>
                    <Typography sx={{ fontFamily: FONT_DATA, fontWeight: 700, fontSize: '0.8125rem', color: colors.textPrimary }}>
                      {formatCurrency(Number(o.total) || 0)}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: '13px' }}>
        <CardLink onClick={openBoard}>Open board →</CardLink>
      </Box>
    </StatBox>
  );
};

/**
 * Read-only quick view for a top seller.
 *
 * Deliberately view-only: editing lives in Product Management, and lifting that
 * dialog out of a 1,900-line page just to reuse it here isn't worth the risk.
 * The catalogue detail is fetched on open; the sales figures come from the board.
 */
const ProductQuickView: React.FC<{ product: TopProduct | null; onClose: () => void }> = ({ product, onClose }) => {
  const { colors, cardStyle } = useAdminTheme();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<ProductDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!product) {
      setDetail(null);
      setFailed(false);
      return;
    }

    let alive = true;
    const query = product.sku || product.name;

    (async () => {
      setLoading(true);
      setFailed(false);
      try {
        const { data } = await authInstance.get('admin/tools/products', {
          params: { page: 1, limit: 10, search: query },
        });
        if (!alive) return;
        const products: ProductDetail[] = data?.products ?? [];
        // Prefer an exact SKU hit; otherwise take the first result.
        const match = products.find((p) => product.sku && p.sku === product.sku) ?? products[0] ?? null;
        setDetail(match);
        setFailed(!match);
      } catch (err) {
        Logger.error('Failed to load product detail:', err);
        if (alive) setFailed(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [product]);

  const image = detail?.imageUrl ?? product?.imageUrl ?? null;

  return (
    <Dialog
      open={!!product}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { ...cardStyle, borderRadius: 3, backgroundImage: 'none' } }}
    >
      {product && (
        <DialogContent sx={{ p: 3 }}>
          <Stack direction="row" spacing={2.5}>
            <Avatar
              src={image ?? undefined}
              alt={product.name}
              variant="rounded"
              sx={{
                width: 104, height: 104, flexShrink: 0,
                bgcolor: '#FFFFFF',
                border: `1px solid ${colors.border}`,
                '& img': { objectFit: 'contain' },
              }}
            >
              <ProductIcon sx={{ fontSize: 34, color: colors.textMuted }} />
            </Avatar>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ color: colors.textPrimary, fontWeight: 800, lineHeight: 1.25 }}>
                {detail?.name ?? product.name}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.textMuted, fontFamily: 'monospace' }}>
                {detail?.sku ?? product.sku ?? '—'}
              </Typography>

              {loading && (
                <Box sx={{ mt: 1.5 }}><CircularProgress size={18} /></Box>
              )}

              {!loading && detail && (
                <Stack direction="row" spacing={0.75} sx={{ mt: 1.25, flexWrap: 'wrap', gap: 0.75 }}>
                  {detail.category && <Chip size="small" label={detail.category} />}
                  {detail.brand && <Chip size="small" label={detail.brand} />}
                  <Chip
                    size="small"
                    label={detail.sellable ? 'Sellable in app' : 'Not sellable'}
                    sx={{
                      bgcolor: alpha(detail.sellable ? colors.success : colors.error, 0.15),
                      color: detail.sellable ? colors.success : colors.error,
                      fontWeight: 700,
                    }}
                  />
                </Stack>
              )}

              {!loading && failed && (
                <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mt: 1.5 }}>
                  Catalogue detail unavailable for this product.
                </Typography>
              )}
            </Box>
          </Stack>

          <Divider sx={{ my: 2.5, borderColor: colors.border }} />

          {/* Sales — straight from the board */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
            <QuickStat label="Units sold" value={String(product.quantity)} color={colors.warning} colors={colors} />
            <QuickStat label="Revenue" value={formatCurrency(product.revenue)} color={colors.success} colors={colors} />
            <QuickStat
              label="Price"
              value={detail ? formatCurrency(detail.priceTier1) : '—'}
              color={colors.info}
              colors={colors}
            />
          </Box>

          {detail?.stock && (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mt: 2 }}>
              <QuickStat
                label="Available"
                value={String(detail.stock.available ?? '—')}
                color={colors.textPrimary}
                colors={colors}
              />
              <QuickStat
                label="On hand"
                value={String(detail.stock.onHand ?? '—')}
                color={colors.textPrimary}
                colors={colors}
              />
              <QuickStat
                label="Status"
                value={detail.status || '—'}
                color={colors.textPrimary}
                colors={colors}
              />
            </Box>
          )}

          {detail?.shortDescription && (
            <Typography variant="body2" sx={{ color: colors.textSecondary, mt: 2.5 }}>
              {detail.shortDescription}
            </Typography>
          )}

          {!!detail?.tags?.length && (
            <Stack direction="row" spacing={0.75} sx={{ mt: 2, flexWrap: 'wrap', gap: 0.75 }}>
              {detail.tags.map((tag) => (
                <Chip key={tag} size="small" variant="outlined" label={tag} sx={{ color: colors.textSecondary }} />
              ))}
            </Stack>
          )}

          <Typography variant="caption" sx={{ display: 'block', mt: 3, color: colors.textMuted }}>
            View only · edit this product in Deeliver Products
          </Typography>
        </DialogContent>
      )}

      {product && (
        <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
          <Button onClick={onClose} sx={{ color: colors.textSecondary }}>
            Close
          </Button>
          <Button
            variant="contained"
            endIcon={<OpenInNewIcon />}
            onClick={() => {
              // Product Management seeds its search box from ?search=, so this
              // lands on the page already filtered to this product.
              const query = detail?.sku || product.sku || product.name;
              navigate(`/products?search=${encodeURIComponent(query)}`);
              onClose();
            }}
            sx={{ bgcolor: colors.warning, color: '#000', fontWeight: 700, '&:hover': { bgcolor: colors.warning } }}
          >
            Go to product
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

const QuickStat: React.FC<{ label: string; value: string; color: string; colors: ReturnType<typeof useAdminTheme>['colors'] }> = ({ label, value, color, colors }) => (
  <Box sx={{ bgcolor: colors.bgElevated, border: `1px solid ${colors.border}`, borderRadius: 2, p: 1.5 }}>
    <Typography variant="caption" sx={{ color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {label}
    </Typography>
    <Typography sx={{ color, fontWeight: 800, fontSize: '1.1rem', mt: 0.25 }}>
      {value}
    </Typography>
  </Box>
);

// ============================================================================
// Main Component
// ============================================================================

const Dashboard: React.FC = () => {
  const { colors, glassHeaderStyle } = useAdminTheme();
  const { role, loading: permissionsLoading, canSeePage, canSeePath } = usePermissions();
  const navigate = useNavigate();
  // Each element has its own tick under Page access → Dashboard (Jason, 31 Oct 2026). Owner and
  // Admin see them all; staff only the ticked ones, with order figures for their stores only.
  const show = (element: string) => role === 'owner' || role === 'admin' || (role === 'staff' && canSeePage(`dashboard.${element}`));
  const STAT_ELEMENTS = ['customers', 'orders', 'revenue', 'avgCart', 'avgDeliveryTime', 'monthToDate'];
  const needsStats = STAT_ELEMENTS.some(show);
  const showTrading = ['livePages', 'stores', ...STAT_ELEMENTS.filter((e) => e !== 'monthToDate')].some(show);
  // One request feeds both new-customer cards, as in the Command Center.
  const { data: signupData, loading: signupsLoading, error: signupsError } = useRecentSignups(!permissionsLoading && show('newCustomers'));
  const [quickView, setQuickView] = useState<TopProduct | null>(null);
  const [data, setData] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      // One call carries every window (`periods`); the top-level KPIs echo `today`.
      const { data: stats } = await authInstance.get('admin/stats', { params: { period: 'today' } });
      setData(stats);
      setError(null);
      setLastUpdate(new Date());
    } catch (err: any) {
      Logger.error('Failed to fetch business stats:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Registered as a react-query query so the app bar's refresh button reaches
   * this page: react-admin's refresh invalidates every query, and the page
   * header no longer carries a second button of its own (the prototype has
   * only the app bar's, plus the stamp here). The interval is the old 60s poll.
   */
  useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: async () => { await fetchStats(); return Date.now(); },
    enabled: !permissionsLoading && needsStats,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    // The stats live in this component's state, not in the query cache, so a
    // remount (rail → Orders → rail → Dashboard) starts with data=null and
    // loading=true. With staleTime: Infinity the cached query counted as fresh
    // and never ran again, leaving the skeleton up until the 60s interval fired.
    refetchOnMount: 'always',
  });

  /**
   * Deeliver Stores — the Store Command Center's own summary, so the two
   * pages never disagree. Closed is derived the way that page derives it:
   * active, not open, not paused.
   */
  const storesQuery = useQuery({
    queryKey: ['dashboard', 'stores'],
    queryFn: async () => (await authInstance.get('/admin/stores/command-center', { params: { includeHidden: true } })).data?.summary as StoresSummary | undefined,
    enabled: !permissionsLoading && show('stores'),
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
  // pausedStores only exists once the pause backend is deployed; until then
  // (staging today) treat it as 0 rather than blowing the page up on undefined.
  const storesSummary = storesQuery.data
    ? { ...storesQuery.data, pausedStores: storesQuery.data.pausedStores ?? 0 }
    : undefined;

  const gateways = usePaged(data?.paymentMethods ?? []);
  const stores = usePaged(data?.topStores ?? []);
  const sellers = usePaged(data?.topProducts ?? [], SELLERS_PAGE_SIZE);

  if (permissionsLoading) {
    return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  }

  const DASHBOARD_ELEMENTS = ['liveUsers', 'liveOrders', 'livePages', ...STAT_ELEMENTS, 'stores', 'newCustomers', 'jumpTo'];
  const hasAccess = DASHBOARD_ELEMENTS.some(show);
  if (!hasAccess) {
    // Staff with no Dashboard ticks work from the pages in their sidebar.
    return (
      <Box sx={{ p: 3 }}>
        <Title title="Dashboard" />
        <Alert severity="info">
          {role === 'staff'
            ? 'Pick a page from the menu. If it is empty, ask an admin to tick your pages under Staff Users → Page access.'
            : 'Access Denied — Owner or Admin role required'}
        </Alert>
      </Box>
    );
  }

  /** Open the read-only quick view for a top seller. */
  const openProduct = (product: TopProduct) => setQuickView(product);

  const topStoreMax = Math.max(1, ...(data?.topStores ?? []).map((s) => s.orders));

  return (
    <Box sx={{ bgcolor: colors.bgDeepest, minHeight: '100vh' }}>
      <Title title="Dashboard" />

      {/* Header — glassHeaderStyle pins it below the fixed app bar, so the
          refresh stays reachable while scrolling. */}
      <Box sx={{ ...glassHeaderStyle, px: 2.75, py: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography
              component="h1"
              sx={{ fontFamily: FONT_DISPLAY, fontStretch: '118%', fontWeight: 800, fontSize: '1.5rem', letterSpacing: '-0.02em', lineHeight: 1, color: colors.textPrimary, m: 0 }}
            >
              Dashboard
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {lastUpdate && (
              <Typography sx={{ fontFamily: FONT_DATA, fontSize: '0.75rem', color: colors.textMuted, display: { xs: 'none', sm: 'block' }, fontVariantNumeric: 'tabular-nums' }}>
                Updated {lastUpdate.toLocaleTimeString()}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ px: 2.75, pb: 8 }}>
        {needsStats && loading && !data ? (
          <Box sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, lg: 4 }}><Skeleton variant="rounded" height={280} sx={{ bgcolor: colors.bgCard }} /></Grid>
              <Grid size={{ xs: 12, lg: 8 }}><Skeleton variant="rounded" height={280} sx={{ bgcolor: colors.bgCard }} /></Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={`box-${i}`}>
                  <Skeleton variant="rounded" height={190} sx={{ bgcolor: colors.bgCard }} />
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : needsStats && error ? (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        ) : data || !needsStats ? (
          <Fade in timeout={300}>
            <Box>
              {/* ---- Right now — the only live band. ---- */}
              {(show('liveUsers') || show('liveOrders')) && (
              <>
              <Band live first>Right now</Band>

              <Grid container spacing={2}>
                {show('liveUsers') && (
                <Grid size={{ xs: 12, lg: show('liveOrders') ? 4 : 12 }}>
                  <LiveUsersBox />
                </Grid>
                )}
                {show('liveOrders') && (
                <Grid size={{ xs: 12, lg: show('liveUsers') ? 8 : 12 }}>
                  <LiveOrdersBox />
                </Grid>
                )}
              </Grid>
              </>
              )}

              {/* ---- Trading — every metric across all four windows at once, so
                   no period control is needed. Live Pages leads the grid, so it
                   sits directly below Live Users and the stat cards flow on
                   after it (the last row can be short). ---- */}
              {showTrading && (
              <Box
                sx={{
                  mt: 2,
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
                }}
              >
                {show('livePages') && <LivePagesBox />}
                {data && show('customers') && <StatCard title="Deeliver Customers" glyph="users" periods={data.periods} pick={(k) => formatCount(k.newCustomers)} />}
                {data && show('orders') && <StatCard title="Deeliver Orders" glyph="cart" periods={data.periods} pick={(k) => formatCount(k.totalOrders)} />}
                {data && show('revenue') && <StatCard title="Revenue" glyph="revenue" periods={data.periods} pick={(k) => formatMoneyCompact(k.totalRevenue)} />}
                {data && show('avgCart') && <StatCard title="Average Cart" glyph="basket" periods={data.periods} pick={(k) => formatMoneyCompact(k.avgCart)} />}
                {data && show('avgDeliveryTime') && <StatCard title="Average Delivery Time" glyph="timer" periods={data.periods} pick={(k) => formatDuration(k.avgDeliverySeconds)} />}
                {show('stores') && (
                <StatBox title="Deeliver Stores" glyph="store" column>
                  <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gridAutoRows: '1fr', gap: 1.5 }}>
                    <StatCell value={storesSummary ? formatCount(storesSummary.openStores) : '—'} label="Open" />
                    <StatCell value={storesSummary ? formatCount(storesSummary.pausedStores) : '—'} label="Paused" />
                    <StatCell value={storesSummary ? formatCount(Math.max(0, storesSummary.activeStores - storesSummary.openStores - storesSummary.pausedStores)) : '—'} label="Closed" />
                    <StatCell value={storesSummary ? formatCount(storesSummary.totalStores) : '—'} label="Total" />
                  </Box>
                </StatBox>
                )}
              </Box>
              )}

              {/* ---- New customers. A rolling window over the latest signups —
                   neither a trading window nor month-to-date, so its own band. ---- */}
              {show('newCustomers') && (
              <>
              <Band>New customers</Band>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <StatBox title="New Customers" glyph="signup">
                    <NewCustomerInfo data={signupData} loading={signupsLoading} error={signupsError} limit={6} />
                  </StatBox>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <StatBox
                    title="New Customer Information"
                    glyph="pie"
                    headerRight={<CardLink onClick={() => navigate('/customers/new')}>View all →</CardLink>}
                  >
                    <NewCustomerOverview data={signupData} loading={signupsLoading} error={signupsError} />
                  </StatBox>
                </Grid>
              </Grid>
              </>
              )}

              {/* ---- Month to date. Stated once here so the three panels below
                   don't each have to explain their own window. ---- */}
              {data && show('monthToDate') && (
              <>
              <Band>This month so far</Band>

              <Grid container spacing={2}>
                {/* Payment Gateways */}
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                  <StatBox title="Payment Gateways" subtitle="By popularity" glyph="card" column>
                    {gateways.slice.length > 0 ? (
                      <Stack spacing={1.5} sx={{ flex: 1 }}>
                        {gateways.slice.map((gateway, idx) => (
                          <Stack key={gateway.method} direction="row" alignItems="center" spacing={1.5}>
                            <RankIdx n={gateways.offset + idx + 1} />
                            <LogoTile
                              src={PAYMENT_LOGOS[gateway.method]}
                              alt={PAYMENT_LABELS[gateway.method] || gateway.method}
                              fallback={<PaymentIcon sx={{ fontSize: 18, color: '#15100F' }} />}
                            />
                            <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: colors.textPrimary, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {PAYMENT_LABELS[gateway.method] || gateway.method}
                            </Typography>
                            <Typography sx={{ fontFamily: FONT_DATA, fontSize: '0.8125rem', fontWeight: 700, color: colors.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
                              {gateway.share}%
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    ) : (
                      <EmptyNote>No payments in this window</EmptyNote>
                    )}
                    <Pager page={gateways.page} pages={gateways.pages} onChange={gateways.setPage} />
                  </StatBox>
                </Grid>

                {/* Top performing stores */}
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                  <StatBox title="Top Performing Stores" subtitle="By orders" glyph="store" column>
                    {stores.slice.length > 0 ? (
                      <Stack spacing={1.5} sx={{ flex: 1 }}>
                        {stores.slice.map((store, idx) => (
                          <Box key={store.storeId}>
                            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: '7px' }}>
                              <RankIdx n={stores.offset + idx + 1} />
                              <LogoTile
                                src={store.isHq ? DEELIVER_MARK : VAPERITE_EMBLEM}
                                alt={store.name}
                                fallback={null}
                              />
                              <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: colors.textPrimary, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {store.name}
                              </Typography>
                              <Typography sx={{ fontFamily: FONT_DATA, fontSize: '0.8125rem', fontWeight: 700, color: colors.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
                                {store.orders}
                              </Typography>
                            </Stack>
                            <Box sx={{ height: 5, borderRadius: 3, bgcolor: 'var(--track)', overflow: 'hidden' }}>
                              <Box sx={{ height: '100%', width: `${(store.orders / topStoreMax) * 100}%`, bgcolor: colors.info, borderRadius: 3 }} />
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <EmptyNote>No orders in this window</EmptyNote>
                    )}
                    <Pager page={stores.page} pages={stores.pages} onChange={stores.setPage} />
                  </StatBox>
                </Grid>

                {/* Top selling items */}
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                  <StatBox title="Top Selling Items" subtitle="By units sold" glyph="layers" column>
                    {sellers.slice.length > 0 ? (
                      <Stack spacing={0.25} sx={{ flex: 1 }}>
                        {sellers.slice.map((product, idx) => (
                          <Stack
                            key={product.productId}
                            direction="row"
                            alignItems="center"
                            spacing={1.4}
                            onClick={() => openProduct(product)}
                            title={`Open ${product.name} in Deeliver Products`}
                            sx={{
                              py: '7px',
                              px: 1,
                              borderRadius: '7px',
                              cursor: 'pointer',
                              '&:hover': {
                                bgcolor: colors.bgElevated,
                                '& .productName': { color: colors.info },
                              },
                            }}
                          >
                            <RankIdx n={sellers.offset + idx + 1} width={16} />
                            <Avatar
                              src={product.imageUrl ?? undefined}
                              alt={product.name}
                              variant="rounded"
                              sx={{
                                width: 30, height: 30, flexShrink: 0,
                                bgcolor: '#FFFFFF',
                                border: `1px solid ${colors.borderGlow}`,
                                borderRadius: '5px',
                                '& img': { objectFit: 'contain' },
                              }}
                            >
                              <ProductIcon sx={{ fontSize: 16, color: '#15100F' }} />
                            </Avatar>
                            <Typography
                              className="productName"
                              sx={{
                                fontSize: '0.84rem',
                                color: colors.textPrimary,
                                fontWeight: 600,
                                flex: 1,
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                transition: 'color .15s',
                              }}
                            >
                              {product.name}
                            </Typography>
                            <Typography sx={{ fontFamily: FONT_DATA, fontSize: '0.72rem', color: colors.textMuted, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                              R {Math.round(product.revenue).toLocaleString('en-ZA')}
                            </Typography>
                            <Typography sx={{ fontFamily: FONT_DATA, fontSize: '0.875rem', fontWeight: 700, color: colors.textPrimary, minWidth: 48, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                              {product.quantity}
                              <Typography component="span" sx={{ fontFamily: FONT_DATA, fontSize: '0.66rem', fontWeight: 400, color: colors.textMuted, ml: '3px' }}>
                                u
                              </Typography>
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    ) : (
                      <EmptyNote>No sales in this window</EmptyNote>
                    )}
                    <Pager page={sellers.page} pages={sellers.pages} onChange={sellers.setPage} />
                  </StatBox>
                </Grid>
              </Grid>
              </>
              )}

              {/* ---- Jump to — the boards this page summarises, at the foot. ---- */}
              {show('jumpTo') && (
              <Box sx={{ mt: 3.25 }}>
                <StatBox title="Jump to" subtitle="Live operational boards" glyph="store">
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                    {QUICK_LINKS.filter((link) => canSeePath(link.to)).map((link) => (
                      <Box
                        key={link.to}
                        component="button"
                        type="button"
                        onClick={() => navigate(link.to)}
                        sx={{
                          font: 'inherit',
                          cursor: 'pointer',
                          textAlign: 'left',
                          px: '14px',
                          py: '10px',
                          minWidth: 196,
                          borderRadius: '8px',
                          bgcolor: colors.bgElevated,
                          border: `1px solid ${colors.borderGlow}`,
                          color: colors.textPrimary,
                          transition: 'border-color 0.15s, transform 0.15s',
                          '&:hover': { borderColor: colors.info, transform: 'translateY(-1px)' },
                          '&:focus-visible': { outline: `2px solid ${colors.info}`, outlineOffset: 2 },
                        }}
                      >
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 650, color: colors.textPrimary, display: 'block' }}>
                          {link.label}
                        </Typography>
                        <Typography sx={{ fontFamily: FONT_DATA, fontSize: '0.6875rem', color: colors.textMuted, display: 'block', mt: '3px' }}>
                          {link.hint}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </StatBox>
              </Box>
              )}
            </Box>
          </Fade>
        ) : null}
      </Box>

      <ProductQuickView product={quickView} onClose={() => setQuickView(null)} />
    </Box>
  );
};

export default Dashboard;
